import { get } from '@vueuse/core';
import isEqual from 'lodash/isEqual';
import { computed, ref } from 'vue';
import type { MaybeRef } from 'vue';
import { deepCompareComputed } from '@/composable/deepCompareComputed';
import { deepFreeze } from '@/core/lib/utils';
import { formatBetBuilderMatch } from '@/core/oddsApi/helpers/formatBetBuilder';
import type { BetBuilderEventFragment, Maybe } from '@/core/oddsApi/oddsApiType';
import { useBetBuilderEventQuery, useBetBuilderEventSubscription } from '@/core/oddsApi/oddsApiType';
import type { IBetBuilderMatch } from '@/interface/betBuilder';

export function useBetBuilderMatch(matchId: MaybeRef<number>, isEnabled = ref(true)) {
    const betBuilderMatch = ref<IBetBuilderMatch | null>();

    const variables = computed(() => ({
        query: {
            id: get(matchId),
        },
    }));

    const { onResult, loaded, loading, refetch } = useBetBuilderEventQuery(
        variables,
        deepCompareComputed(() => ({
            enabled: isEnabled.value,
        })),
    );

    const { onSubscription } = useBetBuilderEventSubscription(
        variables,
        deepCompareComputed(() => ({
            enabled: isEnabled.value,
        })),
    );

    const onUpdate = (result?: Maybe<BetBuilderEventFragment>): void => {
        if (!result) {
            betBuilderMatch.value = null;
            return;
        }

        const newMatch = deepFreeze(formatBetBuilderMatch(result));
        if (!isEqual(betBuilderMatch.value, newMatch)) {
            betBuilderMatch.value = newMatch;
        }
    };
    onResult(onUpdate);
    onSubscription(onUpdate);

    return {
        betBuilderMatch,
        refetch,
        loaded,
        loading,
    };
}
